import { type FunctionComponent, useContext, useEffect, useState } from 'react';
import { DEFAULT_VARIANT_GROUP_NUM, SearchVariantContext } from '../../../contexts/search/search-variant.context';
import { type TrackedEvent } from '../../../helpers/analytics/event-types';
import { getVariantsToDisplay, getTotalGroups } from '../../../helpers/search-variant/search-variant.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { type ProductSearchVariant } from '../../../types/product.types';
import { TextButton } from '../../buttons';
import ChevronLeftIcon from '../../svgs/chevron-left.svg';
import ChevronRightIcon from '../../svgs/chevron-right.svg';
import { DEFAULT_VARIANT_SIZE, SearchVariant } from '../search-variant/search-variant.component';

const SearchVariantRow: FunctionComponent<{
	variants: ProductSearchVariant[];
	className?: string;
	variantSize?: number;
	selectEvent?: TrackedEvent;
}> = ({ variants, className = '', variantSize = DEFAULT_VARIANT_SIZE, selectEvent }) => (
	<div className={className} data-testid={`variants-${variantSize}`}>
		{variants.map((variant) => (
			<SearchVariant
				key={variant.id.toString()}
				variant={variant}
				className={`ma0`}
				variantSize={variantSize}
				selectEvent={selectEvent}
			/>
		))}
	</div>
);

export type SearchVariantSelectorProps = {
	variants: ProductSearchVariant[];
	maxNumberToDisplay: number;
	isTileView: boolean;
	scrollEvent: TrackedEvent;
	selectEvent?: TrackedEvent;
	allowSingleVariant?: boolean;
};
export const SearchVariantSelector: FunctionComponent<SearchVariantSelectorProps> = ({
	variants,
	maxNumberToDisplay,
	allowSingleVariant,
	isTileView,
	scrollEvent,
	selectEvent
}) => {
	const { variantGroupDisplayed, setVariantGroupDisplayed } = useContext(SearchVariantContext);
	const [userAlreadyScrolled, setUserAlreadyScrolled] = useState(false);
	const trackEvent = useTrackEvent();

	useEffect(() => {
		setVariantGroupDisplayed(DEFAULT_VARIANT_GROUP_NUM);
	}, [isTileView, setVariantGroupDisplayed]);

	const totalGroups = getTotalGroups(variants.length, maxNumberToDisplay);
	const showLeftArrow = variantGroupDisplayed > 1;
	const showRightArrow = totalGroups !== variantGroupDisplayed;

	if (!allowSingleVariant && variants.length === 1) {
		return null;
	}

	const slicedVariants = getVariantsToDisplay(variants, maxNumberToDisplay, variantGroupDisplayed);
	const arrowSize = `30px`;

	const onVariantScroll = () => {
		if (userAlreadyScrolled) {
			return;
		}
		setUserAlreadyScrolled(true);
		trackEvent({ event: scrollEvent });
	};

	const handleArrowClick = (increment: number) => {
		setVariantGroupDisplayed(variantGroupDisplayed + increment);
		trackEvent({ event: scrollEvent });
	};

	return (
		<div className={`flex`}>
			<div className={`dn ${showLeftArrow ? 'flex-ns' : ''}`} style={{ width: arrowSize, height: arrowSize, marginRight: '.25rem' }}>
				<TextButton
					buttonStyle="PRIMARY"
					testId="left-variant-arrow"
					disabled={variantGroupDisplayed === 0}
					onClick={() => handleArrowClick(-1)}
					className="pa2">
					<ChevronLeftIcon />
				</TextButton>
			</div>
			<div className="overflow-x-auto overflow-x-visible-ns pb2 pb0-l" data-testid="variant-scroll" onScroll={onVariantScroll}>
				<SearchVariantRow variants={variants} className="flex dn-ns" selectEvent={selectEvent} />
				<SearchVariantRow variants={slicedVariants} className="dn flex-ns" variantSize={22} selectEvent={selectEvent} />
			</div>
			<div className={`dn ${showRightArrow ? 'flex-ns' : ''}`} style={{ width: arrowSize, height: arrowSize }}>
				<TextButton testId="right-variant-arrow" buttonStyle="PRIMARY" onClick={() => handleArrowClick(1)} className="pa2">
					<ChevronRightIcon />
				</TextButton>
			</div>
		</div>
	);
};
